import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import { employeeApi, onboardingApi, docusignApi, extractErrorMessage } from '../../../services/hrApi';
import type { Employee } from '../../../types';
import './StepStyles.scss';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
// @ts-ignore
import draftToHtml from 'draftjs-to-html';
// @ts-ignore
import htmlToDraft from 'html-to-draftjs';
import jsPDF from 'jspdf';

interface DocumentGenerationStepProps {
  employeeId?: string;
  data?: any;
  onDataChange: (data: any) => void;
  onComplete: (isComplete: boolean) => void;
}

type DocumentType = 'offer_letter' | 'nda';
type ActionType = 'upload' | 'generate';

const DocumentGenerationStep: React.FC<DocumentGenerationStepProps> = ({
  employeeId,
  data,
  onDataChange,
  onComplete,
}) => {
  const [activeTab, setActiveTab] = useState<DocumentType>('offer_letter');
  const [activeAction, setActiveAction] = useState<Record<DocumentType, ActionType>>({
    offer_letter: 'upload',
    nda: 'upload',
  });
  const [showGenerateEditor, setShowGenerateEditor] = useState<Record<DocumentType, boolean>>({
    offer_letter: false,
    nda: false,
  });
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState<Record<DocumentType, boolean>>({
    offer_letter: false,
    nda: false,
  });
  const fileInputRefs = useRef<Record<DocumentType, HTMLInputElement | null>>({
    offer_letter: null,
    nda: null,
  });

  const [documents, setDocuments] = useState<Record<DocumentType, {
    id?: string;
    fileUrl?: string;
    fileName?: string;
    fileKey?: string;
    uploadedAt?: string;
    generated?: boolean;
  }>>({
    offer_letter: {},
    nda: {},
  });

  const [documentContent, setDocumentContent] = useState<Record<DocumentType, EditorState>>({
    offer_letter: EditorState.createEmpty(),
    nda: EditorState.createEmpty(),
  });

  // DocuSign envelope state (reused logic from DocumentPackageStep)
  const [existingEnvelope, setExistingEnvelope] = useState<any>(null);
  const [checkingEnvelope, setCheckingEnvelope] = useState(false);
  const [creatingEnvelope, setCreatingEnvelope] = useState(false);

  useEffect(() => {
    if (employeeId) {
      fetchEmployee();
      fetchDocuments();
      // Check for existing DocuSign envelope draft affecting these documents
      checkExistingEnvelope();
    }
  }, [employeeId]);

  // Store callbacks in refs to avoid dependency issues
  const onCompleteRef = useRef(onComplete);
  const onDataChangeRef = useRef(onDataChange);
  
  // Update refs when callbacks change
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onDataChangeRef.current = onDataChange;
  }, [onComplete, onDataChange]);

  const isCompleteRef = useRef(false);
  const prevDocumentsRef = useRef(documents);
  
  useEffect(() => {
    const isComplete = !!(documents.offer_letter.fileUrl && documents.nda.fileUrl);
    
    // Check if documents actually changed
    const documentsChanged = 
      prevDocumentsRef.current.offer_letter.fileUrl !== documents.offer_letter.fileUrl ||
      prevDocumentsRef.current.offer_letter.fileName !== documents.offer_letter.fileName ||
      prevDocumentsRef.current.offer_letter.id !== documents.offer_letter.id ||
      prevDocumentsRef.current.nda.fileUrl !== documents.nda.fileUrl ||
      prevDocumentsRef.current.nda.fileName !== documents.nda.fileName ||
      prevDocumentsRef.current.nda.id !== documents.nda.id;
    
    // Only call callbacks if something actually changed
    if (documentsChanged) {
      prevDocumentsRef.current = documents;
      
      // Only call onComplete if the completion status actually changed
      if (isComplete !== isCompleteRef.current) {
        isCompleteRef.current = isComplete;
        onCompleteRef.current(isComplete);
      }
      
      onDataChangeRef.current({
        offer_letter: documents.offer_letter,
        nda: documents.nda,
      });
    }
  }, [documents]);

  const fetchEmployee = async () => {
    if (!employeeId) return;
    try {
      setLoading(true);
      const emp = await employeeApi.getEmployee(employeeId);
      setEmployee(emp);
    } catch (error) {
      console.error('Error fetching employee:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    if (!employeeId) return;
    try {
      const docs = await employeeApi.getEmployeeDocuments(employeeId);
      const documentsMap: Record<DocumentType, {
        id?: string;
        fileUrl?: string;
        fileName?: string;
        fileKey?: string;
        uploadedAt?: string;
        generated?: boolean;
      }> = {
        offer_letter: {},
        nda: {},
      };

      if (Array.isArray(docs) && docs.length > 0) {
        docs.forEach((doc: any) => {
          if (doc.type === 'offer_letter' || doc.type === 'nda') {
            documentsMap[doc.type as DocumentType] = {
              id: doc.id,
              fileUrl: doc.fileUrl,
              fileName: doc.name,
              fileKey: doc.path,
              uploadedAt: doc.updated_at || doc.created_at,
              generated: false, // We can't determine this from the API, but uploaded documents are not generated
            };
          }
        });
      }

      // If no documents found in DB, fall back to data prop (localStorage)
      if (!documentsMap.offer_letter.fileUrl && !documentsMap.nda.fileUrl && data) {
        documentsMap.offer_letter = data?.offer_letter || {};
        documentsMap.nda = data?.nda || {};
      }

      setDocuments(documentsMap);
    } catch (error) {
      console.error('Error fetching documents:', error);
      // Fall back to data prop if fetch fails
      if (data) {
        setDocuments({
          offer_letter: data?.offer_letter || {},
          nda: data?.nda || {},
        });
      }
    }
  };

  const getS3Path = (documentType: DocumentType, fileName: string): string => {
    let userId = 'unknown';
    
    // Get user ID from userdata (contains full user object with id)
    const userDataString = localStorage.getItem('userdata');
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        userId = userData?.id || userData?.user_id || userData?.uid || 'unknown';
      } catch (e) {
        console.warn('Failed to parse userdata:', e);
      }
    }
    
    // Fallback to authUser (username) if userdata doesn't have id
    if (userId === 'unknown') {
      const authUser = localStorage.getItem('authUser');
      if (authUser) {
        // authUser is stored as a string (username) in JWT auth
        // Use it as userId if userdata doesn't have id
        userId = authUser;
      }
    }

    const extension = fileName.split('.').pop() || 'pdf';
    const documentName = documentType === 'offer_letter' ? 'Offer Letter' : 'NDA';
    const fileNameWithEmployeeId = `${documentName}-${employeeId}.${extension}`;
    
    return `HR Portal/${userId}/Onboarding Documents/${employeeId}/${fileNameWithEmployeeId}`;
  };

  const checkExistingEnvelope = async () => {
    if (!employeeId) return;
    setCheckingEnvelope(true);
    try {
      const result = await docusignApi.getEnvelopeDraft(employeeId as string, 'employee-onboarding');
      if (result.exists) {
        setExistingEnvelope(result);
      } else {
        setExistingEnvelope(null);
      }
    } catch (error) {
      console.error('Error checking existing envelope for document generation:', error);
      setExistingEnvelope(null);
    } finally {
      setCheckingEnvelope(false);
    }
  };

  const handleDeleteDraft = async (): Promise<boolean> => {
    if (!employeeId) return false;

    if (!window.confirm('Are you sure you want to delete the existing DocuSign envelope draft? You will need to recreate it in the Document Package step.')) {
      return false;
    }

    setCreatingEnvelope(true);
    try {
      const result = await docusignApi.deleteEnvelopeDraft({
        employee_id: employeeId,
        envelope_type: 'employee-onboarding',
      });

      if (result.success) {
        toast.success('Envelope draft deleted. You can now change the documents and recreate the envelope from the Document Package step.');
        setExistingEnvelope(null);
        return true;
      }

      toast.error(`Failed to delete envelope draft: ${result.error || 'Unknown error'}`);
      return false;
    } catch (error: any) {
      console.error('Error deleting envelope draft:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to delete envelope draft: ${errorMessage}`);
      return false;
    } finally {
      setCreatingEnvelope(false);
    }
  };

  const ensureEnvelopeAllowsDocumentChange = async (actionDescription: string): Promise<boolean> => {
    const envelopeStatus = existingEnvelope?.status;

    // No envelope or already voided: safe to proceed
    if (!existingEnvelope || envelopeStatus === 'voided') {
      return true;
    }

    // Draft envelope: require deleting draft
    if (envelopeStatus === 'created' || envelopeStatus === 'tabs_placed') {
      const label =
        envelopeStatus === 'created'
          ? 'Created'
          : 'Tabs Placed';

      if (
        !window.confirm(
          `A DocuSign envelope draft exists for this employee (status: ${label}). ` +
          `${actionDescription} will require deleting the existing draft. ` +
          `You will need to recreate the envelope from the Document Package step. Do you want to continue?`
        )
      ) {
        return false;
      }

      const draftDeleted = await handleDeleteDraft();
      return draftDeleted;
    }

    // Sent or completed: user must void/delete from package flow
    if (envelopeStatus === 'sent' || envelopeStatus === 'completed') {
      toast.error(
        `There is an active DocuSign envelope for this employee (status: ${envelopeStatus}). ` +
        `${actionDescription} is not allowed while the envelope is active. ` +
        'Please void or delete the envelope from the Document Package step and then try again.'
      );
      return false;
    }

    // Fallback: block by default
    toast.error(
      `There is a DocuSign envelope for this employee (status: ${envelopeStatus}). ` +
      `${actionDescription} is not allowed while the envelope is active. ` +
      'Please manage the envelope from the Document Package step.'
    );
    return false;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>, documentType: DocumentType) => {
    const file = e.target.files?.[0];
    if (!file || !employeeId) return;

    // Ensure envelope state allows changing documents
    const canChange = await ensureEnvelopeAllowsDocumentChange('Replacing this file');
    if (!canChange) {
      // Reset input so same file can be selected again later
      if (fileInputRefs.current[documentType]) {
        fileInputRefs.current[documentType]!.value = '';
      }
      return;
    }

    setUploading((prev) => ({ ...prev, [documentType]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('employee_id', employeeId);
      formData.append('document_type', documentType);
      
      // Get S3 path for new file
      const s3Path = getS3Path(documentType, file.name);
      formData.append('s3_path', s3Path);
      
      // Get old file path if replacing existing document
      const currentDoc = documents[documentType];
      if (currentDoc.fileKey) {
        formData.append('old_file_path', currentDoc.fileKey);
      }

      // Call upload API using axios
      const result = await onboardingApi.uploadDocument(formData);

      setDocuments((prev) => ({
        ...prev,
        [documentType]: {
          id: result.documentId,
          fileUrl: result.fileUrl,
          fileName: result.fileName || file.name, // Use the formatted name from backend
          fileKey: result.fileKey,
          uploadedAt: new Date().toISOString(),
          generated: false,
        },
      }));

      // Hide the editor after successful upload
      setShowGenerateEditor((prev) => ({ ...prev, [documentType]: false }));
    } catch (error: any) {
      console.error(`Error uploading ${documentType}:`, error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to upload file: ${errorMessage}`);
    } finally {
      setUploading((prev) => ({ ...prev, [documentType]: false }));
      // Reset file input
      if (fileInputRefs.current[documentType]) {
        fileInputRefs.current[documentType]!.value = '';
      }
    }
  };

  const generateOfferLetterTemplate = (): string => {
    const employeeName = employee ? `${employee.first_name} ${employee.last_name}` : 'Employee';
    const role = employee?.position || 'Position';
    const department = employee?.department || 'Department';
    const employeeType = employee?.employee_type === 'w2' ? 'W2' : '1099';
    const classification = employee?.classification === 'exempt' ? 'Exempt' : 'Non-Exempt';
    const startDate = employee?.hire_date ? new Date(employee.hire_date).toLocaleDateString() : new Date().toLocaleDateString();
    const salary = employee?.salary_rate || employee?.salary || 'To be discussed';

    const template = `
OFFER LETTER

Date: ${new Date().toLocaleDateString()}

Dear ${employeeName},

We are pleased to offer you the position of ${role} at [Company Name], reporting to the ${department} department.

POSITION DETAILS:
- Position: ${role}
- Department: ${department}
- Employment Type: ${employeeType}
- Classification: ${classification}
- Start Date: ${startDate}
- Compensation: ${salary}

TERMS AND CONDITIONS:
${employeeType === 'W2' ? `
- This is a full-time position with standard benefits including health insurance, retirement plan, and paid time off.
- Employment is at-will and may be terminated by either party with appropriate notice.
` : `
- This is a contractor position. You will be responsible for your own taxes and benefits.
- Payment will be made according to submitted invoices and agreed terms.
`}

${classification === 'Exempt' ? `
- As an exempt employee, you will not be eligible for overtime compensation.
` : `
- As a non-exempt employee, you will be eligible for overtime compensation as required by law.
`}

Please indicate your acceptance of this offer by signing and returning this letter by [Date].

We look forward to welcoming you to our team!

Sincerely,
[Hiring Manager Name]
[Company Name]

Employee Acceptance:
I accept the terms of this offer letter.

Signature: _________________________ Date: _____________
${employeeName}
    `.trim();

    return template;
  };

  const generateNDATemplate = (): string => {
    const employeeName = employee ? `${employee.first_name} ${employee.last_name}` : 'Employee';
    const classification = employee?.classification === 'exempt' ? 'exempt' : 'non-exempt';

    const template = `
NON-DISCLOSURE AGREEMENT (NDA)

This Non-Disclosure Agreement ("Agreement") is entered into on ${new Date().toLocaleDateString()} between [Company Name] ("Company") and ${employeeName} ("Employee").

CONFIDENTIAL INFORMATION:
Employee acknowledges that during the course of employment, they may have access to and become acquainted with various trade secrets, inventions, innovations, processes, information, records and specifications owned or licensed by the Company.

OBLIGATIONS:
1. Employee agrees to hold and maintain in confidence all confidential information.
2. Employee will not disclose confidential information to any third parties.
3. Employee will use confidential information solely for Company business purposes.
4. Upon termination, Employee will return all confidential materials.

NON-COMPETE (if applicable):
${classification === 'exempt' ? `
Employee agrees not to engage in competing business activities during employment and for 12 months after termination within the geographic area where Company operates.
` : `
Standard confidentiality obligations apply without additional non-compete restrictions.
`}

DURATION:
This agreement remains in effect during employment and for 3 years after termination.

REMEDIES:
Company may seek injunctive relief and monetary damages for any breach of this agreement.

Employee Signature: _________________________ Date: _____________
${employeeName}

Company Representative: _________________________ Date: _____________
[Name, Title]
    `.trim();

    return template;
  };

  const stripHtmlTags = (html: string): string => {
    // Create a temporary div element to parse HTML
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    
    // Get text content and preserve line breaks
    let text = tmp.textContent || tmp.innerText || '';
    
    // Replace multiple spaces with single space
    text = text.replace(/\s+/g, ' ');
    
    // Replace common HTML entities
    text = text.replace(/&nbsp;/g, ' ');
    text = text.replace(/&amp;/g, '&');
    text = text.replace(/&lt;/g, '<');
    text = text.replace(/&gt;/g, '>');
    text = text.replace(/&quot;/g, '"');
    text = text.replace(/&#39;/g, "'");
    
    return text.trim();
  };

  const createPDFFromContent = async (editorState: EditorState, documentType: DocumentType): Promise<Blob> => {
    // Convert EditorState to HTML
    const htmlContent = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    
    // Parse HTML to extract text content
    const tmp = document.createElement('DIV');
    tmp.innerHTML = htmlContent;
    
    const title = documentType === 'offer_letter' ? 'OFFER LETTER' : 'NON-DISCLOSURE AGREEMENT';
    
    // Create a new jsPDF instance
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    
    // Set up margins and page dimensions
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - (margin * 2);
    let yPosition = margin;
    const lineHeight = 7;
    
    // Helper function to add text with word wrapping
    const addText = (text: string, fontSize: number = 12, isBold: boolean = false, isItalic: boolean = false) => {
      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', isBold ? 'bold' : isItalic ? 'italic' : 'normal');
      
      // Split text into lines that fit within the page width
      const lines = pdf.splitTextToSize(text, maxWidth);
      
      // Check if we need a new page
      if (yPosition + (lines.length * lineHeight) > pageHeight - margin) {
        pdf.addPage();
        yPosition = margin;
      }
      
      // Add each line
      lines.forEach((line: string) => {
        pdf.text(line, margin, yPosition);
        yPosition += lineHeight;
      });
    };
    
    // Add title
    addText(title, 16, true);
    yPosition += 5; // Extra space after title
    
    // Process HTML content
    const processElement = (element: Element): void => {
      const tagName = element.tagName.toLowerCase();
      const text = element.textContent?.trim() || '';
      
      if (!text) return;
      
      // Handle different HTML tags
      if (tagName === 'h1' || tagName === 'h2') {
        addText(text, 14, true);
        yPosition += 3;
      } else if (tagName === 'h3' || tagName === 'h4') {
        addText(text, 12, true);
        yPosition += 2;
      } else if (tagName === 'strong' || tagName === 'b') {
        addText(text, 12, true);
        yPosition += 2;
      } else if (tagName === 'em' || tagName === 'i') {
        addText(text, 12, false, true);
        yPosition += 2;
      } else if (tagName === 'p') {
        // Check for nested formatting
        const hasBold = element.querySelector('strong, b');
        const hasItalic = element.querySelector('em, i');
        
        if (hasBold || hasItalic) {
          // Process child nodes to preserve formatting (inline)
          // Build the paragraph text with formatting preserved
          let currentX = margin;
          Array.from(element.childNodes).forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE) {
              const nodeText = node.textContent || '';
              if (nodeText.trim()) {
                addText(nodeText, 12);
              }
            } else if (node.nodeType === Node.ELEMENT_NODE) {
              const el = node as Element;
              const elTag = el.tagName.toLowerCase();
              const elText = el.textContent || '';
              if (elText.trim()) {
                if (elTag === 'strong' || elTag === 'b') {
                  addText(elText, 12, true);
                } else if (elTag === 'em' || elTag === 'i') {
                  addText(elText, 12, false, true);
                } else {
                  addText(elText, 12);
                }
              }
            }
          });
        } else {
          addText(text, 12);
        }
        yPosition += 2; // Space between paragraphs
      } else if (tagName === 'br') {
        yPosition += lineHeight;
      } else {
        // Default: add as regular text
        addText(text, 12);
        yPosition += 2;
      }
    };
    
    // Process all paragraph elements
    const pElements = tmp.querySelectorAll('p');
    if (pElements.length > 0) {
      pElements.forEach((p) => {
        processElement(p);
      });
    } else {
      // If no <p> tags, process the entire content
      const plainText = stripHtmlTags(htmlContent);
      const lines = plainText.split('\n').filter(line => line.trim());
      lines.forEach(line => {
        addText(line.trim(), 12);
        yPosition += 2;
      });
    }
    
    // Generate PDF blob
    const pdfBlob = pdf.output('blob');
    return pdfBlob;
  };

  const handleGenerateTemplate = (documentType: DocumentType) => {
    if (!employeeId || !employee) {
      toast.error('Employee information is required to generate documents');
      return;
    }

    // Generate template content (plain text)
    const templateContent = documentType === 'offer_letter' 
      ? generateOfferLetterTemplate() 
      : generateNDATemplate();

    // Convert plain text to HTML format for Draft.js
    // Replace line breaks with <br> tags and wrap paragraphs
    const htmlContent = templateContent
      .split('\n')
      .map(line => line.trim() === '' ? '<br>' : `<p>${line}</p>`)
      .join('');

    // Convert HTML to Draft.js EditorState
    const blocksFromHtml = htmlToDraft(htmlContent);
    const { contentBlocks, entityMap } = blocksFromHtml;
    const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
    const editorState = EditorState.createWithContent(contentState);

    // Set the content in the editor
    setDocumentContent((prev) => ({
      ...prev,
      [documentType]: editorState,
    }));

    // Switch to generate tab if not already
    setActiveAction((prev) => ({ ...prev, [documentType]: 'generate' }));
  };

  const handleSaveDocument = async (documentType: DocumentType) => {
    if (!employeeId || !employee) {
      toast.error('Employee information is required to save documents');
      return;
    }

    const editorState = documentContent[documentType];
    
    // Check if content is empty
    const currentContent = editorState.getCurrentContent();
    const plainText = currentContent.getPlainText();
    if (!plainText || plainText.trim() === '') {
      toast.error('Please generate or enter document content before saving');
      return;
    }

    // Ensure envelope state allows regenerating documents
    const canChange = await ensureEnvelopeAllowsDocumentChange('Regenerating this document');
    if (!canChange) {
      return;
    }

    setUploading((prev) => ({ ...prev, [documentType]: true }));

    try {
      // Create PDF from edited content
      const pdfBlob = await createPDFFromContent(editorState, documentType);
      
      // Create a File object from the blob
      const fileName = `${documentType === 'offer_letter' ? 'Offer Letter' : 'NDA'}-${employeeId}.pdf`;
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

      // Upload the generated PDF
      const formData = new FormData();
      formData.append('file', pdfFile);
      formData.append('employee_id', employeeId);
      formData.append('document_type', documentType);
      
      // Get S3 path
      const s3Path = getS3Path(documentType, fileName);
      formData.append('s3_path', s3Path);
      
      // Get old file path if replacing existing document
      const currentDoc = documents[documentType];
      if (currentDoc.fileKey) {
        formData.append('old_file_path', currentDoc.fileKey);
      }

      // Upload using the same upload API
      const result = await onboardingApi.uploadDocument(formData);

      setDocuments((prev) => ({
        ...prev,
        [documentType]: {
          id: result.documentId,
          fileUrl: result.fileUrl,
          fileName: result.fileName || fileName,
          fileKey: result.fileKey,
          uploadedAt: new Date().toISOString(),
          generated: true,
        },
      }));

      // Hide the editor after successful save
      setShowGenerateEditor((prev) => ({ ...prev, [documentType]: false }));

      toast.success('Document saved successfully!');
    } catch (error: any) {
      console.error(`Error saving ${documentType}:`, error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to save document: ${errorMessage}`);
    } finally {
      setUploading((prev) => ({ ...prev, [documentType]: false }));
    }
  };


  const handleReplaceFile = (documentType: DocumentType) => {
    setActiveAction((prev) => ({ ...prev, [documentType]: 'upload' }));
    setShowGenerateEditor((prev) => ({ ...prev, [documentType]: false }));
    fileInputRefs.current[documentType]?.click();
  };

  const handleDeleteDocument = async (documentType: DocumentType) => {
    const doc = documents[documentType];
    if (!doc.fileKey || !doc.id) {
      toast.error('Document information not available');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the ${documentType === 'offer_letter' ? 'Offer Letter' : 'NDA'}? This action cannot be undone.`)) {
      return;
    }

    setUploading((prev) => ({ ...prev, [documentType]: true }));

    try {
      // Delete from backend (database and S3)
      await employeeApi.deleteEmployeeDocument(doc.id);
      
      // Clear document from state
      setDocuments((prev) => ({
        ...prev,
        [documentType]: {},
      }));

      // Clear editor content
      setDocumentContent((prev) => ({
        ...prev,
        [documentType]: EditorState.createEmpty(),
      }));

      // Hide generate editor if shown
      setShowGenerateEditor((prev) => ({ ...prev, [documentType]: false }));

      toast.success('Document deleted successfully');
    } catch (error: any) {
      console.error('Error deleting document:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to delete document: ${errorMessage}`);
    } finally {
      setUploading((prev) => ({ ...prev, [documentType]: false }));
    }
  };

  const handleGenerateClick = (documentType: DocumentType) => {
    setShowGenerateEditor((prev) => ({ ...prev, [documentType]: true }));
    // Always generate a fresh template, replacing any existing content
    handleGenerateTemplate(documentType);
  };

  const handleCancelGenerate = (documentType: DocumentType) => {
    setShowGenerateEditor((prev) => ({ ...prev, [documentType]: false }));
  };

  if (loading) {
    return (
      <div className="wizard-step">
        <div className="step-header">
          <h4>Document Generation</h4>
          <p>Generate offer letter and NDA documents</p>
        </div>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading employee information...</p>
        </div>
      </div>
    );
  }

  if (!employeeId) {
    return (
      <div className="wizard-step">
        <div className="step-header">
          <h4>Document Generation</h4>
          <p>Generate offer letter and NDA documents</p>
        </div>
        <div className="step-info-box">
          <i className="bx bx-info-circle"></i>
          <p>Please complete the Basic Details step and create the employee record first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-step">
      <div className="step-header">
        <h4>Document Generation</h4>
        <p>Generate offer letter and NDA documents</p>
      </div>

      <div className="step-form">
        {/* Tabs */}
        <div className="document-tabs">
          <button
            className={`tab-button ${activeTab === 'offer_letter' ? 'active' : ''}`}
            onClick={() => setActiveTab('offer_letter')}
          >
            <i className="bx bx-file-blank"></i>
            Offer Letter
            {documents.offer_letter.fileUrl && (
              <i className="bx bx-check-circle tab-check"></i>
            )}
          </button>
          <button
            className={`tab-button ${activeTab === 'nda' ? 'active' : ''}`}
            onClick={() => setActiveTab('nda')}
          >
            <i className="bx bx-file"></i>
            NDA
            {documents.nda.fileUrl && (
              <i className="bx bx-check-circle tab-check"></i>
            )}
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* File input - always rendered but hidden */}
          <input
            ref={(el) => (fileInputRefs.current[activeTab] = el)}
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={(e) => handleFileSelect(e, activeTab)}
            style={{ display: 'none' }}
          />
          
          {(activeTab === 'offer_letter' ? documents.offer_letter : documents.nda).fileUrl ? (
            // Document already uploaded/generated
            <div className="document-status">
              <div className="document-status-header">
                {uploading[activeTab] ? (
                  <>
                    <i className="bx bx-loader-alt bx-spin status-icon"></i>
                    <h5>
                      {activeTab === 'offer_letter' ? 'Offer Letter' : 'NDA'} - Uploading...
                    </h5>
                  </>
                ) : (
                  <>
                    <i className="bx bx-check-circle status-icon"></i>
                    <h5>
                      {activeTab === 'offer_letter' ? 'Offer Letter' : 'NDA'} 
                      {(activeTab === 'offer_letter' ? documents.offer_letter : documents.nda).generated 
                        ? ' Generated' 
                        : ' Uploaded'}
                    </h5>
                  </>
                )}
              </div>
              <div className="document-info">
                <p>
                  <strong>File:</strong> {(activeTab === 'offer_letter' ? documents.offer_letter : documents.nda).fileName}
                </p>
                {(activeTab === 'offer_letter' ? documents.offer_letter : documents.nda).uploadedAt && (
                  <p>
                    <strong>Date:</strong> {new Date(
                      (activeTab === 'offer_letter' ? documents.offer_letter : documents.nda).uploadedAt!
                    ).toLocaleDateString()}
                  </p>
                )}
              </div>
              <div className="document-actions">
                <button
                  onClick={async () => {
                    const doc = activeTab === 'offer_letter' ? documents.offer_letter : documents.nda;
                    if (doc.fileKey) {
                      try {
                        const result = await onboardingApi.getDocumentDownloadUrl(doc.fileKey, 3600);
                        if (result.fileUrl) {
                          window.open(result.fileUrl, '_blank');
                        } else {
                          toast.error('Failed to get download URL');
                        }
                      } catch (error: any) {
                        console.error('Error getting download URL:', error);
                        toast.error('Failed to download document');
                      }
                    }
                  }}
                  className="btn-view-document"
                >
                  <i className="bx bx-download"></i>
                  Download Document
                </button>
                <button
                  type="button"
                  className="btn-replace-document"
                  onClick={(e) => {
                    e.preventDefault();
                    handleReplaceFile(activeTab);
                  }}
                  disabled={uploading[activeTab]}
                >
                  {uploading[activeTab] ? (
                    <>
                      <i className="bx bx-loader-alt bx-spin"></i>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="bx bx-upload"></i>
                      Replace File
                    </>
                  )}
                </button>
                {showGenerateEditor[activeTab] ? (
                  <button
                    type="button"
                    className="btn-replace-document"
                    onClick={(e) => {
                      e.preventDefault();
                      handleCancelGenerate(activeTab);
                    }}
                    disabled={uploading[activeTab]}
                  >
                    <i className="bx bx-x"></i>
                    Cancel
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn-generate-document"
                    onClick={(e) => {
                      e.preventDefault();
                      handleGenerateClick(activeTab);
                    }}
                    disabled={uploading[activeTab]}
                  >
                    <i className="bx bx-wand"></i>
                    Generate
                  </button>
                )}
                <button
                  type="button"
                  className="btn-delete-document"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDeleteDocument(activeTab);
                  }}
                  disabled={uploading[activeTab]}
                >
                  {uploading[activeTab] ? (
                    <>
                      <i className="bx bx-loader-alt bx-spin"></i>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="bx bx-trash"></i>
                      Delete
                    </>
                  )}
                </button>
              </div>
              
              {/* Show editor when Generate is clicked */}
              {showGenerateEditor[activeTab] && (
                <div style={{ marginTop: '24px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
                  <h5 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: 600, color: '#212529' }}>
                    Edit & Generate Document
                  </h5>
                  <p style={{ marginBottom: '16px', fontSize: '14px', color: '#6c757d' }}>
                    Edit the document content below and click "Save Document" to regenerate and replace the existing document.
                  </p>
                  
                  <div className="document-editor-container">
                    <div style={{ marginBottom: '10px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                      {!documentContent[activeTab] || 
                       !documentContent[activeTab].getCurrentContent().getPlainText().trim() ? (
                        <button
                          className="btn-generate-template"
                          onClick={() => handleGenerateTemplate(activeTab)}
                          disabled={uploading[activeTab] || !employee}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#f0f0f0',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: employee && !uploading[activeTab] ? 'pointer' : 'not-allowed',
                            opacity: employee && !uploading[activeTab] ? 1 : 0.6
                          }}
                        >
                          <i className="bx bx-wand" style={{ marginRight: '5px' }}></i>
                          Generate Template
                        </button>
                      ) : null}
                      {documentContent[activeTab] && 
                       documentContent[activeTab].getCurrentContent().getPlainText().trim() && (
                        <button
                          className="btn-save-document"
                          onClick={() => handleSaveDocument(activeTab)}
                          disabled={uploading[activeTab] || !documentContent[activeTab].getCurrentContent().getPlainText().trim()}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: !uploading[activeTab] && documentContent[activeTab].getCurrentContent().getPlainText().trim() ? 'pointer' : 'not-allowed',
                            opacity: !uploading[activeTab] && documentContent[activeTab].getCurrentContent().getPlainText().trim() ? 1 : 0.6
                          }}
                        >
                          {uploading[activeTab] ? (
                            <>
                              <i className="bx bx-loader-alt bx-spin" style={{ marginRight: '5px' }}></i>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="bx bx-save" style={{ marginRight: '5px' }}></i>
                              Save Document
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    
                    <div style={{ border: '1px solid #ddd', borderRadius: '4px', background: '#fff' }}>
                      <Editor
                        editorState={documentContent[activeTab]}
                        onEditorStateChange={(newEditorState) => {
                          setDocumentContent((prev) => ({
                            ...prev,
                            [activeTab]: newEditorState,
                          }));
                        }}
                        toolbarClassName="toolbarClassName"
                        wrapperClassName="wrapperClassName"
                        editorClassName="editorClassName"
                        placeholder="Click 'Generate Template' to create a template, then edit it and click 'Save Document' to regenerate and replace the document"
                        readOnly={uploading[activeTab]}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            // No document yet - show upload/generate options
            <div className="document-actions-selector">
              <div className="action-tabs">
                <button
                  className={`action-tab ${activeAction[activeTab] === 'upload' ? 'active' : ''}`}
                  onClick={() => setActiveAction((prev) => ({ ...prev, [activeTab]: 'upload' }))}
                >
                  <i className="bx bx-upload"></i>
                  Upload
                </button>
                <button
                  className={`action-tab ${activeAction[activeTab] === 'generate' ? 'active' : ''}`}
                  onClick={() => setActiveAction((prev) => ({ ...prev, [activeTab]: 'generate' }))}
                >
                  <i className="bx bx-edit"></i>
                  Generate Here
                </button>
              </div>

              <div className="action-content">
                {activeAction[activeTab] === 'upload' ? (
                  <div className="upload-section">
                    <div className="upload-area">
                      <i className="bx bx-cloud-upload upload-icon"></i>
                      <h5>Upload {activeTab === 'offer_letter' ? 'Offer Letter' : 'NDA'}</h5>
                      <p>Select a file to upload (PDF, DOC, or DOCX)</p>
                      <button
                        className="btn-upload"
                        onClick={() => fileInputRefs.current[activeTab]?.click()}
                        disabled={uploading[activeTab]}
                      >
                        {uploading[activeTab] ? (
                          <>
                            <i className="bx bx-loader-alt bx-spin"></i>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <i className="bx bx-folder-open"></i>
                            Choose File
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="generate-section">
                    <div className="generate-info">
                      <i className="bx bx-edit-alt generate-icon"></i>
                      <h5>Generate {activeTab === 'offer_letter' ? 'Offer Letter' : 'NDA'}</h5>
                      <p>
                        Generate a new {activeTab === 'offer_letter' ? 'offer letter' : 'NDA'} document 
                        based on the employee's information from Basic Details. You can edit the template before saving.
                      </p>
                    </div>
                    
                    <div className="document-editor-container" style={{ marginTop: '20px' }}>
                      <div style={{ marginBottom: '10px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                        {!documentContent[activeTab] || 
                         !documentContent[activeTab].getCurrentContent().getPlainText().trim() ? (
                          <button
                            className="btn-generate-template"
                            onClick={() => handleGenerateTemplate(activeTab)}
                            disabled={uploading[activeTab] || !employee}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#f0f0f0',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              cursor: employee && !uploading[activeTab] ? 'pointer' : 'not-allowed',
                              opacity: employee && !uploading[activeTab] ? 1 : 0.6
                            }}
                          >
                            <i className="bx bx-wand" style={{ marginRight: '5px' }}></i>
                            Generate Template
                          </button>
                        ) : null}
                        {documentContent[activeTab] && 
                         documentContent[activeTab].getCurrentContent().getPlainText().trim() && (
                          <button
                            className="btn-save-document"
                            onClick={() => handleSaveDocument(activeTab)}
                            disabled={uploading[activeTab] || !documentContent[activeTab].getCurrentContent().getPlainText().trim()}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#007bff',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: !uploading[activeTab] && documentContent[activeTab].getCurrentContent().getPlainText().trim() ? 'pointer' : 'not-allowed',
                              opacity: !uploading[activeTab] && documentContent[activeTab].getCurrentContent().getPlainText().trim() ? 1 : 0.6
                            }}
                          >
                            {uploading[activeTab] ? (
                              <>
                                <i className="bx bx-loader-alt bx-spin" style={{ marginRight: '5px' }}></i>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="bx bx-save" style={{ marginRight: '5px' }}></i>
                                Save Document
                              </>
                            )}
                          </button>
                        )}
                      </div>
                      
                      <div style={{ border: '1px solid #ddd', borderRadius: '4px' }}>
                        <Editor
                          editorState={documentContent[activeTab]}
                          onEditorStateChange={(newEditorState) => {
                            setDocumentContent((prev) => ({
                              ...prev,
                              [activeTab]: newEditorState,
                            }));
                          }}
                          toolbarClassName="toolbarClassName"
                          wrapperClassName="wrapperClassName"
                          editorClassName="editorClassName"
                          placeholder="Click 'Generate Template' to create a template, then edit it and click 'Save Document'"
                          readOnly={uploading[activeTab]}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentGenerationStep;
